import dayjs from 'dayjs';

import { parseRelativeBound } from './dateBounds';
import { allowsMultipleDates, precisionOf } from './dateOptions';
import { normalizeCurrency, toMajor } from './currency';
import { validateElementValue } from './elementValidation';
import { normalizeColor } from './elementFormat/color';
import { allowsMultipleChoices, canonicalChoices, choiceListOf } from './choiceValues';
import { normalizeMeasurement } from './units';
import { clockIn, dayIn, isKnownZone, relativeDefaultValue } from './zonedClock';
import { PLATFORM_TIMEZONE } from '../config/timezones';

/**
 * ELEMENT DEFAULTS — the web half (docs/plans/data-types-second-pass.md §3.6,
 * PR 4; the owner's rulings D7 and D8).
 *
 * `options.defaultValue` is the value a NEW item starts with. The API applies
 * it on every create path, when the create does not mention the element
 * (keepr-api utils/elementDefaults.js). The web applies the SAME default to the
 * forms that open a new item — the item form, the public form, multi add, the
 * quick-create modal — through `getElementInitialValue`, which asks here, so a
 * person sees the value the server would have written and a create that omits
 * the key stores the same thing as one that sends it.
 *
 * The rule, the server's, read in the browser:
 *   - `defaultToToday` (a date) and `defaultToNow` (a date-time, a time) win
 *     over a `defaultValue`;
 *   - a date or date-time default is `{ kind: 'relative', value: 'today+30d' }`
 *     (the bound grammar, utils/dateBounds) or `{ kind: 'literal', value }`;
 *   - "today", "now" on a time and a relative date are read in the
 *     COLLECTION's time zone, exactly as the server reads them (the owner's
 *     ruling B11, 2026-10-01; utils/zonedClock) — never the browser's: a
 *     person in UTC adding to a Pacific collection late in its evening gets
 *     the Pacific day the server would store. The caller passes the zone
 *     (utils/collectionTimezone collectionZone); without one, keepr's default;
 *   - a currency default is `{ value, currency }` in major units, and the form
 *     holds `{ amount, currency }` in minor units (the shared normaliser);
 *   - a measurement default is `{ value, unit }` (the owner's ruling B12,
 *     2026-10-01: the server applies it too), held to the measure, the
 *     element's units and min / max by the vendored normaliser the API runs;
 *   - a default that no longer fits the element's rules is NOT seeded: the
 *     field starts blank, exactly as the server leaves the element blank. The
 *     create is never refused because of the author's default.
 *   - a server-owned element (driven, numbered), a unique one and an account
 *     link never take one.
 *
 * Nothing here is vendored: the grammar is the API's (`parseRelative`) and the
 * web's (`parseRelativeBound`) — the same one, pinned by both suites' vectors
 * in elementDefaults.test.js — and a month that overruns stops on the last day
 * on both sides (utils/zonedClock addCalendarUnits; the API's addCalendarUnits).
 */

// The types that take `options.defaultValue` (keepr-api utils/elementDefaults.js
// DEFAULT_TYPES — the same list, in the same order).
export const DEFAULT_TYPES = [
	'text-small', 'text-large', 'choice', 'boolean', 'rating',
	'number', 'decimal', 'integer',
	'date', 'date-time', 'time',
	'phone', 'email', 'url', 'currency',
	// B12: a measurement's { value, unit }, server-applied like every other.
	'measurement',
	// PR 9: a color, stored as "#rrggbb".
	'color',
];

export const takesDefault = dataType => DEFAULT_TYPES.includes(dataType);

const isPlainObject = v => v !== null && typeof v === 'object' && !Array.isArray(v);
const isBlank = v => v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);

/** The author's `defaultValue`, or undefined when none is stated. */
export function explicitDefault(element) {
	const raw = element?.options?.defaultValue;
	return isBlank(raw) ? undefined : raw;
}

/** 'today' | 'now' | null — the date toggles, which win over a defaultValue. */
export function clockDefault(element) {
	const options = element?.options || {};
	if (element?.dataType === 'date' && options.defaultToToday === true) return 'today';
	if ((element?.dataType === 'date-time' || element?.dataType === 'time') && options.defaultToNow === true) return 'now';
	return null;
}

/** Does this element state a default the server applies? */
export function hasDefault(element) {
	return !!element && takesDefault(element.dataType) && (clockDefault(element) !== null || explicitDefault(element) !== undefined);
}

/** Would a create consider it at all? Never on a server-owned, unique or account-link element. */
export function isEligible(element) {
	const options = element?.options || {};
	return hasDefault(element) && !options.drivenFrom && !options.sequence && options.unique !== true && options.identity !== true;
}

/** A Choice element's values (legacy bare strings included); [] on any other type, whose list is inert. */
export function choiceValues(element) {
	return choiceListOf(element)
		.map(c => (c && typeof c === 'object' ? String(c.value ?? '') : String(c ?? '')))
		.filter(Boolean);
}

/** A date / date-time default's stored option: { value } | { relative } | null (malformed). */
function dateDefaultShape(element, raw) {
	if (!isPlainObject(raw) || (raw.kind !== 'literal' && raw.kind !== 'relative') || typeof raw.value !== 'string') return null;
	if (raw.kind === 'relative') {
		const parts = parseRelativeBound(raw.value);
		// `now` names an instant, which a day does not have.
		if (!parts || (parts.anchor === 'now' && element?.dataType !== 'date-time')) return null;
		return { relative: raw.value };
	}
	return { value: raw.value };
}

/** The zone a default is read in: the one given when this browser knows it, else keepr's default. */
const zoneOf = zone => (isKnownZone(zone) ? zone : PLATFORM_TIMEZONE);

/**
 * Does this element's default read a clock — today, now or a relative date —
 * so its value depends on the zone (keepr-api utils/elementDefaults.js
 * needsZone)? A form waits for the collection's zone before seeding one.
 */
export function needsZone(element) {
	if (!isEligible(element)) return false;
	if (clockDefault(element)) return true;
	const raw = explicitDefault(element);
	return isPlainObject(raw) && raw.kind === 'relative';
}

/**
 * The default in the shape a FORM holds, before the element's rules judge it,
 * or undefined. `now` is injectable (a dayjs, a Date or an ISO string) so a
 * test can hold the clock; `zone` is the collection's (B11).
 */
export function resolveDefault(element, { now, zone } = {}) {
	if (!element) return undefined;
	const instant = now ? dayjs(now).toDate() : new Date();
	const tz = zoneOf(zone);
	const clock = clockDefault(element);
	if (clock === 'today') return dayIn(tz, instant);
	if (clock === 'now') return element.dataType === 'time' ? clockIn(tz, instant) : instant.toISOString();
	const raw = explicitDefault(element);
	if (raw === undefined) return undefined;
	const dataType = element.dataType;
	if (dataType === 'date' || dataType === 'date-time') {
		const shape = dateDefaultShape(element, raw);
		if (!shape) return undefined;
		if (shape.relative) return relativeDefaultValue(shape.relative, dataType, instant, tz) ?? undefined;
		return shape.value;
	}
	if (dataType === 'currency') {
		if (!isPlainObject(raw) || typeof raw.value !== 'number' || typeof raw.currency !== 'string') return undefined;
		try {
			return normalizeCurrency({ value: raw.value, currency: raw.currency }, element.options || {}) || undefined;
		} catch (e) {
			return undefined;
		}
	}
	return raw;
}

/** A date's value as the element stores it: a precision floor, a one-entry list for a multi-value date. */
function dateShape(element, value) {
	let out = value;
	if (element.dataType === 'date' && typeof out === 'string') {
		const precision = precisionOf(element);
		if (precision === 'month') out = `${out.slice(0, 7)}-01`;
		else if (precision === 'year') out = `${out.slice(0, 4)}-01-01`;
	}
	return allowsMultipleDates(element) ? [out] : out;
}

/**
 * Does the element take `value` NOW — its own rules, with the checks the
 * API's default gate makes (the element's validator, a choice's list, a real
 * true/false)? -> a sentence, or null.
 */
export function defaultProblem(element, value, { now } = {}) {
	const dataType = element?.dataType;
	if (dataType === 'boolean') return typeof value === 'boolean' ? null : 'A yes/no default is Yes or No.';
	if (dataType === 'measurement') return measurementDefaultProblem(element, value);
	// "Allow multiple" (PR 5): the default is a LIST of the choices, every
	// entry one of them (keepr-api utils/elementDefaults.js checkChoice).
	if (dataType === 'choice' && element?.options?.allowMultiple === true) {
		const entries = Array.isArray(value) ? value : [value];
		if (entries.some(entry => typeof entry !== 'string')) return 'Choose from the choices.';
		const values = choiceValues(element);
		if (values.length && entries.some(entry => !values.includes(entry))) return 'The default is no longer among the choices.';
		return null;
	}
	// A Choice only: a short text's default is text, whatever list it carries
	// (an inert one, kept for a change back to Choice — KPE-033).
	if (dataType === 'choice') {
		if (typeof value !== 'string') return 'Choose one of the choices.';
		const values = choiceValues(element);
		if (values.length && !values.includes(value)) return 'The default is no longer one of the choices.';
		return null;
	}
	if (['text-small', 'text-large', 'phone', 'email', 'url'].includes(dataType) && typeof value !== 'string') return 'The default must be text.';
	if (dataType === 'color' && typeof value !== 'string') return 'The default must be a color.';
	if (['number', 'decimal', 'integer', 'rating'].includes(dataType) && typeof value !== 'number') return 'The default must be a number.';
	const context = { elements: [element], values: {}, now: now ? dayjs(now) : undefined };
	return validateElementValue(element, value, context);
}

/**
 * A measurement default: the OBJECT `{ value, unit }` the editor stores (a
 * `base` beside them is tolerated) — never a bare number left from a number
 * element's days, nor typed text — then the write path's own normaliser
 * (keepr-api utils/elementDefaults.js checkMeasurement, the same rule).
 */
function measurementDefaultProblem(element, value) {
	if (!isPlainObject(value) || typeof value.value !== 'number' || !Number.isFinite(value.value) || typeof value.unit !== 'string'
		|| Object.keys(value).some(k => k !== 'value' && k !== 'unit' && k !== 'base')) {
		return 'The default needs a number and a unit.';
	}
	try {
		return normalizeMeasurement({ value: value.value, unit: value.unit }, element?.options || {}) ? null : 'The default needs a number and a unit.';
	} catch (e) {
		const why = String(e?.message || 'check the unit and the limits').replace(/\.$/, '');
		return `The default does not fit: ${why}.`;
	}
}

/**
 * elementDefaultValue(element, { now, zone }) -> the value a new item's form opens
 * the element at, or undefined (no default, or one that no longer fits — the
 * field then starts as it always did).
 */
export function elementDefaultValue(element, { now, zone } = {}) {
	if (!isEligible(element)) return undefined;
	const resolved = resolveDefault(element, { now, zone });
	if (resolved === undefined) return undefined;
	// A measurement opens as the pair the input holds; the API adds `base`.
	if (element.dataType === 'measurement') {
		return defaultProblem(element, resolved) ? undefined : { value: resolved.value, unit: resolved.unit };
	}
	const value = (element.dataType === 'date' || element.dataType === 'date-time')
		? dateShape(element, resolved)
		// A list default opens the form in the choices' order, a single one
		// under "Allow multiple" as a list of one — the stored shape.
		: (allowsMultipleChoices(element) ? canonicalChoices(element, resolved) : resolved);
	if (defaultProblem(element, value, { now })) return undefined;
	// A color opens at its stored spelling — what the API's create stores
	// (a default an API author wrote as "DodgerBlue" is "#1e90ff" on the item).
	if (element.dataType === 'color') return normalizeColor(value).value;
	return value;
}

/**
 * The editor's side: a currency default is stored `{ value, currency }` in
 * major units, the entry control holds `{ amount, currency }` in minor units.
 */
export function currencyDefaultToInput(raw, options) {
	if (!isPlainObject(raw) || typeof raw.value !== 'number' || typeof raw.currency !== 'string') return null;
	try {
		return normalizeCurrency({ value: raw.value, currency: raw.currency }, { ...(options || {}), nonNegative: false, min: undefined, max: undefined }) || null;
	} catch (e) {
		return null;
	}
}

export function currencyInputToDefault(value) {
	if (!isPlainObject(value) || !Number.isInteger(value.amount) || typeof value.currency !== 'string') return undefined;
	const major = toMajor(value.amount, value.currency);
	return Number.isFinite(major) ? { value: major, currency: value.currency } : undefined;
}

/**
 * What the editor says about a STORED default right now: null when it fits (or
 * there is none), else the sentence to show under the Default value field —
 * the same question the API asks at save (400 invalid_options { option:
 * 'defaultValue' }), so the refusal is seen before the round trip. A relative
 * date is settled at each create: only its grammar is checked here.
 */
export function storedDefaultProblem(element, { now } = {}) {
	const raw = explicitDefault(element);
	if (raw === undefined || !takesDefault(element?.dataType)) return null;
	const options = element?.options || {};
	if (options.drivenFrom) return "A driven or calculated element can't have a default — clear it to save.";
	if (options.unique === true) return "A unique element can't have a default — a value every new item starts with could only be held by one item. Clear it to save.";
	if (options.sequence) return "A numbered element can't have a default — clear it to save.";
	const dataType = element.dataType;
	if (dataType === 'date' || dataType === 'date-time') {
		const shape = dateDefaultShape(element, raw);
		if (!shape) return 'The default is not a date this element can hold.';
		if (shape.relative) return null;
		// Authoring judges a literal against the element's LITERAL bounds only.
		const literalOnly = { ...element, options: { ...options } };
		['min', 'max'].forEach((key) => {
			if (isPlainObject(literalOnly.options[key]) && literalOnly.options[key].kind !== 'literal') delete literalOnly.options[key];
		});
		return defaultProblem(literalOnly, dateShape(element, shape.value), { now });
	}
	if (dataType === 'currency') {
		if (!isPlainObject(raw) || typeof raw.value !== 'number' || typeof raw.currency !== 'string') return 'The default amount needs a number and a currency.';
		try {
			normalizeCurrency({ value: raw.value, currency: raw.currency }, options);
			return null;
		} catch (e) {
			return `The default amount does not fit: ${String(e?.message || 'check the currency and the limits')}.`;
		}
	}
	return defaultProblem(element, raw, { now });
}

// What a default is judged against — keepr-api utils/elementDefaults.js
// RULE_KEYS, the same list: a change to any of these re-asks the default at
// save, a change to anything else (the help text, a choice's colour) does
// not. THE ECHO: a save that changes neither an element's default nor these
// saves, even over a stored default that never fit.
export const RULE_KEYS = [
	'drivenFrom', 'identity', 'unique', 'sequence',
	'maxLength', 'case', 'trim', 'pattern', 'extendedLength', 'choices', 'allowMultiple',
	'max', 'count', 'allowHalf', 'decimals', 'nonNegative', 'min',
	'weekdays', 'precision', 'country', 'currencies', 'defaultCurrency',
	// A measurement's (B12): its unit is of the measure and among the units,
	// and min / max are read in the defaultUnit.
	'measure', 'units', 'defaultUnit',
];

function stableJson(value) {
	if (Array.isArray(value)) return `[${value.map(stableJson).join(',')}]`;
	if (isPlainObject(value)) return `{${Object.keys(value).sort().map(k => `${JSON.stringify(k)}:${stableJson(value[k])}`).join(',')}}`;
	return JSON.stringify(value === undefined ? null : value);
}

function ruleView(element) {
	const options = element?.options || {};
	const view = { dataType: element?.dataType };
	RULE_KEYS.forEach((key) => {
		const v = options[key];
		if (v === undefined || v === null) return;
		if (key === 'drivenFrom' || key === 'sequence') view[key] = !!v;
		else if (key === 'choices') view.choices = choiceValues(element);
		else view[key] = v;
	});
	return view;
}

/** Neither the default nor the rules it is judged by moved since `saved` was stored. */
export function unchangedDefault(element, saved) {
	if (!saved || !element || (saved.dataType || 'text-small') !== (element.dataType || 'text-small')) return false;
	return stableJson(saved?.options?.defaultValue) === stableJson(element?.options?.defaultValue)
		&& stableJson(ruleView(saved)) === stableJson(ruleView(element));
}
